import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiActivity, 
  FiCheckCircle, 
  FiClock, 
  FiAlertCircle,
  FiTrendingUp,
  FiLayers,
  FiUsers,
  FiCalendar,
  FiRefreshCw,
  FiMonitor,
  FiSettings,
  FiXCircle
} from 'react-icons/fi';
import PropertiesApi from '../../baseUrl/PropertiesApi';
import './Dashboard.css';

interface PropertyData {
  group_id: string;
  property_id: string;
  date: string;
  error_statuses?: (string | null)[];
  app_names: string[];
  app_statuses: string[];
}

interface PropertiesResponse {
  properties: PropertyData[];
}

interface AppStats {
  [appName: string]: number;
}

// Helper function to get current year
const getCurrentYear = (): number => {
  return new Date().getFullYear();
};

// Helper function to get first day of current year
const getFirstDayOfYear = (): string => {
  const year = getCurrentYear();
  return `${year}-01-01`;
};

// Helper function to get last day of current year
const getLastDayOfYear = (): string => {
  const year = getCurrentYear();
  return `${year}-12-31`;
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date for display
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = parseInt(month) - 1;
  const monthName = monthNames[monthIndex] || month;
  
  return `${monthName} ${parseInt(day)}, ${year}`;
};

// Helper function to format date for display (MM/DD/YYYY)
const formatDateDisplay = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${month}/${day}/${year}`;
};

// Helper function to format time ago
const getTimeAgo = (dateStr: string): string => {
  const now = new Date();
  const today = getTodayDate();
  
  if (dateStr === today) {
    return 'Today';
  }
  
  const date = new Date(dateStr);
  const diffTime = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return formatDate(dateStr);
};

// Helper function to format app name for display
const formatAppName = (appName: string): string => {
  return appName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [stats, setStats] = useState({
    dailyAutomate: 0,
    completed: 0,
    scheduled: 0,
    pending: 0,
    failed: 0
  });
  const [appStats, setAppStats] = useState<{
    completed: AppStats;
    scheduled: AppStats;
    pending: AppStats;
    failed: AppStats;
  }>({
    completed: {},
    scheduled: {},
    pending: {},
    failed: {}
  });

  // Date range state - default to Jan 1 to Dec 31 of current year
  const [startDate, setStartDate] = useState(getFirstDayOfYear());
  const [endDate, setEndDate] = useState(getLastDayOfYear());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Click outside to close date picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch properties data
  const fetchProperties = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    }
    
    try {
      const options = {
        limit: 200,
        length: 2,
        page: 1,
        start_id: '',
        end_id: '',
        start_date: startDate,
        end_date: endDate,
        order_by: 'date',
        search: ''
      };
      
      const data: PropertiesResponse = await PropertiesApi.GetAllAutomateProperties(options);
      const propertiesData = data.properties || [];
      setProperties(propertiesData);
      setLastUpdated(new Date());
      
      // Calculate stats
      let completedCount = 0;
      let scheduledCount = 0;
      let pendingCount = 0;
      let failedCount = 0;
      
      const completedByApp: AppStats = {};
      const scheduledByApp: AppStats = {};
      const pendingByApp: AppStats = {};
      const failedByApp: AppStats = {};
      
      propertiesData.forEach((property: PropertyData) => {
        property.app_statuses.forEach((status: string, idx: number) => {
          const statusLower = status.toLowerCase();
          const appName = property.app_names[idx] || 'Unknown';
          
          if (statusLower === 'completed') {
            completedCount++;
            completedByApp[appName] = (completedByApp[appName] || 0) + 1;
          } else if (statusLower === 'scheduled') {
            scheduledCount++;
            scheduledByApp[appName] = (scheduledByApp[appName] || 0) + 1;
          } else if (statusLower === 'pending') {
            pendingCount++;
            pendingByApp[appName] = (pendingByApp[appName] || 0) + 1;
          } else if (statusLower === 'failed') {
            failedCount++;
            failedByApp[appName] = (failedByApp[appName] || 0) + 1;
          }
        });
      });
      
      setStats({
        dailyAutomate: propertiesData.length,
        completed: completedCount,
        scheduled: scheduledCount,
        pending: pendingCount,
        failed: failedCount
      });
      
      setAppStats({
        completed: completedByApp,
        scheduled: scheduledByApp,
        pending: pendingByApp,
        failed: failedByApp
      });
      
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  }, [startDate, endDate]);

  // Initial load
  useEffect(() => {
    fetchProperties(true);
  }, [fetchProperties]);

  // Real-time 30-second refresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchProperties(false);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchProperties]);

  // Format date range for display
  const formatDateRange = () => {
    if (startDate === endDate) {
      return formatDateDisplay(startDate);
    }
    return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
  };

  // Reset to default date range
  const resetDateRange = () => {
    setStartDate(getFirstDayOfYear());
    setEndDate(getLastDayOfYear());
    setShowDatePicker(false);
  };

  // Apply date range
  const applyDateRange = () => {
    setShowDatePicker(false);
    fetchProperties(true);
  };

  // Format app stats for display
  const formatAppStats = (appStatsObj: AppStats): string => {
    const entries = Object.entries(appStatsObj);
    if (entries.length === 0) return 'No data';
    return entries.map(([app, count]) => `${formatAppName(app)}: ${count}`).join(' • ');
  };

  // Get recent activity (activities with status changes)
  const getRecentActivity = () => {
    const activities: { id: string; action: string; time: string; status: string; propertyId: string }[] = [];
    
    properties.forEach(property => {
      property.app_names.forEach((appName, idx) => {
        const status = property.app_statuses[idx]?.toLowerCase() || 'pending';
        
        let action = '';
        let activityStatus = status;
        
        if (status === 'completed') {
          action = `${formatAppName(appName)} completed for Property ${property.property_id}`;
          activityStatus = 'success';
        } else if (status === 'failed') {
          action = `${formatAppName(appName)} failed for Property ${property.property_id}`;
          activityStatus = 'failed';
        } else if (status === 'starting') {
          action = `${formatAppName(appName)} started for Property ${property.property_id}`;
          activityStatus = 'processing';
        } else if (status === 'scheduled') {
          action = `${formatAppName(appName)} scheduled for Property ${property.property_id}`;
          activityStatus = 'scheduled';
        }
        
        if (action && (status === 'completed' || status === 'failed' || status === 'starting')) {
          activities.push({
            id: `${property.group_id}-${idx}`,
            action,
            time: getTimeAgo(property.date),
            status: activityStatus,
            propertyId: property.property_id
          });
        }
      });
    });
    
    // Sort by status priority (processing > completed > failed) and limit to 8
    const statusPriority: { [key: string]: number } = { processing: 0, success: 1, failed: 2, scheduled: 3 };
    activities.sort((a, b) => (statusPriority[a.status] || 4) - (statusPriority[b.status] || 4));
    
    return activities.slice(0, 8);
  };

  // Get upcoming/scheduled tasks - only scheduled status
  const getUpcomingTasks = () => {
    const tasks: { id: string; task: string; time: string; propertyId: string; appName: string }[] = [];
    
    properties.forEach(property => {
      property.app_names.forEach((appName, idx) => {
        const status = property.app_statuses[idx]?.toLowerCase() || 'pending';
        
        // Only show scheduled tasks
        if (status === 'scheduled') {
          tasks.push({
            id: `${property.group_id}-${idx}`,
            task: `${formatAppName(appName)} - Property ${property.property_id}`,
            time: formatDate(property.date),
            propertyId: property.property_id,
            appName
          });
        }
      });
    });
    
    return tasks.slice(0, 6);
  };

  // Quick action handlers
  const handleSyncProperties = () => {
    fetchProperties(true);
  };

  const handleManageUsers = () => {
    navigate('/users');
  };

  const handleViewLogs = () => {
    navigate('/app-login-activity');
  };

  const handleViewAutomation = () => {
    navigate('/automation');
  };

  const handleRemoteServer = () => {
    navigate('/remote-server');
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  const recentActivity = getRecentActivity();
  const upcomingTasks = getUpcomingTasks();

  const statsConfig = [
    {
      title: 'Daily Automate',
      value: stats.dailyAutomate.toString(),
      icon: <FiActivity />,
      color: 'blue',
      change: `${properties.length} properties`
    },
    {
      title: 'Completed Tasks',
      value: stats.completed.toString(),
      icon: <FiCheckCircle />,
      color: 'green',
      change: formatAppStats(appStats.completed),
      appStats: appStats.completed
    },
    {
      title: 'Scheduled Tasks',
      value: stats.scheduled.toString(),
      icon: <FiClock />,
      color: 'purple',
      change: formatAppStats(appStats.scheduled),
      appStats: appStats.scheduled
    },
    {
      title: 'Pending Tasks',
      value: stats.pending.toString(),
      icon: <FiAlertCircle />,
      color: stats.failed > 0 ? 'red' : 'orange',
      change: formatAppStats(appStats.pending),
      appStats: appStats.pending
    }
  ];

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <FiRefreshCw className="loading-spinner spin" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p className="dashboard-subtitle">Welcome back! Here's what's happening with your automations.</p>
        </div>
        <div className="dashboard-header-actions">
          {/* Date Range Picker */}
          <div className="date-picker-container" ref={datePickerRef}>
            <div 
              className={`date-picker-trigger ${startDate !== getFirstDayOfYear() || endDate !== getLastDayOfYear() ? 'active' : ''}`}
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <FiCalendar />
              <span>{formatDateRange()}</span>
            </div>

            {showDatePicker && (
              <div className="date-picker-dropdown">
                <div className="date-picker-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate}
                  />
                </div>
                <div className="date-picker-field">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                  />
                </div>
                <div className="date-picker-actions">
                  <button className="btn-reset" onClick={resetDateRange}>
                    Reset
                  </button>
                  <button className="btn-apply" onClick={applyDateRange}>
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {lastUpdated && (
            <span className="last-updated-badge">
              <FiRefreshCw className="spin-slow" />
              Live • {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statsConfig.map((stat, index) => (
          <div key={index} className={`stat-card stat-${stat.color}`}>
            <div className="stat-icon">
              {stat.icon}
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stat.value}</h3>
              <p className="stat-title">{stat.title}</p>
              {stat.appStats && Object.keys(stat.appStats).length > 0 ? (
                <div className="stat-app-breakdown">
                  {Object.entries(stat.appStats).map(([app, count]) => (
                    <span key={app} className="app-count-badge">
                      {formatAppName(app)}: {count}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="stat-change">{stat.change}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Recent Activity */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2><FiActivity /> Recent Activity</h2>
            <button className="view-all-btn" onClick={() => navigate('/automation')}>View All</button>
          </div>
          <div className="activity-list">
            {recentActivity.length === 0 ? (
              <div className="empty-state">
                <FiActivity className="empty-icon" />
                <p>No activity in selected date range</p>
              </div>
            ) : (
              recentActivity.map(activity => (
                <div key={activity.id} className="activity-item">
                  <div className={`activity-status status-${activity.status}`}>
                    {activity.status === 'success' && <FiCheckCircle />}
                    {activity.status === 'processing' && <FiClock />}
                    {activity.status === 'failed' && <FiXCircle />}
                    {activity.status === 'scheduled' && <FiCalendar />}
                  </div>
                  <div className="activity-details">
                    <p className="activity-action">{activity.action}</p>
                    <span className="activity-time">{activity.time}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Upcoming Tasks */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2><FiCalendar /> Upcoming Tasks</h2>
            <button className="view-all-btn" onClick={() => navigate('/automation')}>View Schedule</button>
          </div>
          <div className="tasks-list">
            {upcomingTasks.length === 0 ? (
              <div className="empty-state">
                <FiCalendar className="empty-icon" />
                <p>No scheduled tasks</p>
              </div>
            ) : (
              upcomingTasks.map(task => (
                <div key={task.id} className="task-item">
                  <div className="task-icon">
                    <FiClock />
                  </div>
                  <div className="task-details">
                    <p className="task-name">{task.task}</p>
                    <span className="task-time">{task.time}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2><FiTrendingUp /> Quick Actions</h2>
          </div>
          <div className="quick-actions">
            <button className="action-btn" onClick={handleSyncProperties}>
              <FiRefreshCw />
              <span>Refresh Data</span>
            </button>
            <button className="action-btn" onClick={handleViewAutomation}>
              <FiLayers />
              <span>View Properties</span>
            </button>
            <button className="action-btn" onClick={handleViewLogs}>
              <FiActivity />
              <span>Login Activity</span>
            </button>
            <button className="action-btn" onClick={handleRemoteServer}>
              <FiMonitor />
              <span>Remote Server</span>
            </button>
            <button className="action-btn" onClick={handleManageUsers}>
              <FiUsers />
              <span>Manage Users</span>
            </button>
            <button className="action-btn" onClick={handleSettings}>
              <FiSettings />
              <span>Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
